<script setup lang="ts">
/**
 * Floating action bar — vertical pill with phone, chat, and notes buttons.
 * Replaces 3 separate floating buttons with a single cohesive bar.
 *
 * Collapsed by default behind ONE round launcher (W12-DI, 2026-10-01): the
 * open four-button pill sat over the right edge of every page — the atlas
 * Calculator table and its scrollbar, the carrier profile's cards and their
 * "Hide" buttons. The launcher expands the same pill, unchanged; Escape, a
 * click outside, choosing an action, or a route change collapses it. The
 * launcher carries every indicator the pill shows (phone status dot, unread
 * badge, amber report dot) so nothing goes quiet while it is closed.
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePhoneStore } from '@claimshield/shared'
import { useSupportStore } from '../stores/supportStore'

const phone = usePhoneStore()
const support = useSupportStore()
const route = useRoute()

const expanded = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const launcherEl = ref<HTMLButtonElement | null>(null)

function collapse(refocus = false) {
  if (!expanded.value) return
  expanded.value = false
  if (refocus) launcherEl.value?.focus()
}

function onDocPointerDown(e: PointerEvent) {
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) collapse()
}
function onDocKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') collapse(true)
}

// Document listeners live only while the pill is open.
watch(expanded, (open) => {
  if (open) {
    document.addEventListener('pointerdown', onDocPointerDown, true)
    document.addEventListener('keydown', onDocKeydown)
  } else {
    document.removeEventListener('pointerdown', onDocPointerDown, true)
    document.removeEventListener('keydown', onDocKeydown)
  }
})
onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  document.removeEventListener('keydown', onDocKeydown)
})

// The bar is mounted once in App.vue and outlives navigation; open state is not
// carried from one page to the next.
watch(() => route?.fullPath, () => collapse())

// Each action runs its handler exactly as before, then the pill folds away.
function act(run: () => void) {
  run()
  collapse()
}

// Report nudge — a small card that slides out to the LEFT of the bar when an
// error is captured, then retracts back toward the buttons. It ASKS
// ("Something feel off?") rather than asserting a failure, because the capture
// often fires on transient errors the user never noticed. The persistent amber
// dot on the megaphone remains until the issue is reported or dismissed; the
// card and its button both open the same prefilled report dialog.
//
// Dwell was 30s, which read as a stuck modal rather than a passing offer —
// long enough that it was still on screen after a session-expiry redirect had
// moved the app to /login. 6s is a glance-and-decide window; nothing is lost by
// missing it, because the amber dot on the megaphone is the durable signal and
// it survives until the error is reported or dismissed.
const NUDGE_TUCK_MS = 6_000
const cardVisible = ref(false)
let tuckTimer: ReturnType<typeof setTimeout> | null = null
watch(
  () => support.nudge,
  (n) => {
    if (tuckTimer) clearTimeout(tuckTimer)
    if (n) {
      cardVisible.value = true
      tuckTimer = setTimeout(() => { cardVisible.value = false }, NUDGE_TUCK_MS)
    } else {
      cardVisible.value = false
    }
  },
  { immediate: true }
)
onUnmounted(() => { if (tuckTimer) clearTimeout(tuckTimer) })

// Dismiss the card only (keep the amber dot so it can still be reported later
// via the megaphone). Reporting from the card/megaphone clears the dot.
function dismissCard() { cardVisible.value = false }

// Hide the whole bar while a right-side panel (chat / notes) is open — pinned at
// bottom-right it would otherwise float over the panel's input row and collide
// with its send button. The open panel carries its own controls.
const props = defineProps<{ panelOpen?: boolean }>()

const emit = defineEmits<{
  toggleChat: []
  toggleNotes: []
  toggleRequest: []
}>()

// The bar (and the nudge card) hide while a panel is open or the phone is expanded.
const barHidden = computed(() => phone.isExpanded || props.panelOpen)

// Phone status color matching PhoneWidget's statusColor
const phoneStatusColor = computed(() => {
  switch (phone.status) {
    case 'ready': return 'bg-green-500'
    case 'on-call': return 'bg-green-500 animate-pulse'
    case 'ringing': return 'bg-amber-500 animate-pulse'
    case 'offline': return 'bg-gray-400'
    case 'error': return 'bg-red-500'
    default: return 'bg-gray-400'
  }
})
</script>

<template>
  <div
    v-show="!barHidden"
    ref="rootEl"
    class="fixed bottom-6 right-6 z-50 flex flex-col-reverse items-center gap-3"
  >
    <!-- Launcher — the only thing on screen until opened. It repeats every
         indicator the pill carries: phone status dot (top-right), unread badge
         (top-left), amber report dot (bottom-right). -->
    <button
      ref="launcherEl"
      type="button"
      data-testid="fab-launcher"
      class="relative flex h-12 w-12 items-center justify-center rounded-full bg-primary-700 text-white shadow-xl transition-colors hover:bg-primary-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-600/40 motion-reduce:transition-none"
      :aria-expanded="expanded"
      aria-controls="fab-actions"
      :aria-label="expanded ? 'Close quick actions' : 'Quick actions'"
      @click="expanded = !expanded"
    >
      <i :class="['pi text-lg', expanded ? 'pi-times' : 'pi-plus']" aria-hidden="true" />
      <div
        data-testid="fab-launcher-phone-dot"
        :class="['absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white', phoneStatusColor]"
      />
      <div
        v-if="phone.unreadCount > 0"
        data-testid="fab-launcher-unread"
        class="absolute -top-0.5 -left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[12px] font-bold text-white"
      >
        {{ phone.unreadCount > 9 ? '9+' : phone.unreadCount }}
      </div>
      <div
        v-if="support.nudge"
        data-testid="fab-launcher-nudge-dot"
        class="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500"
      />
    </button>

    <Transition
      enter-active-class="motion-safe:transition-all motion-safe:duration-150 motion-safe:ease-out"
      enter-from-class="motion-safe:translate-y-2 opacity-0"
      leave-active-class="motion-safe:transition-all motion-safe:duration-100 motion-safe:ease-in"
      leave-to-class="motion-safe:translate-y-2 opacity-0"
    >
    <div
      v-if="expanded"
      id="fab-actions"
      data-testid="fab-actions"
      class="flex flex-col items-center gap-0 rounded-full shadow-xl border border-gray-200 overflow-hidden"
      style="background: #ffffff;"
    >
    <!-- Chat button -->
    <button
      class="flex h-12 w-12 items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors"
      aria-label="Ask AI"
      @click="act(() => emit('toggleChat'))"
      v-tooltip.left="'Ask AI'"
    >
      <i class="pi pi-comment text-lg" />
    </button>

    <div class="h-px w-6 bg-gray-200" />

    <!-- Phone button -->
    <button
      class="relative flex h-12 w-12 items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors"
      aria-label="Phone"
      @click="act(() => { phone.isExpanded = !phone.isExpanded })"
      v-tooltip.left="'Phone'"
    >
      <i :class="['text-lg', phone.isOnCall ? 'pi pi-phone animate-pulse' : 'pi pi-phone']" />
      <!-- Status dot -->
      <div :class="['absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full border-2 border-white', phoneStatusColor]" />
      <!-- Unread badge -->
      <div
        v-if="phone.unreadCount > 0"
        class="absolute -top-0.5 -left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[12px] font-bold text-white"
      >
        {{ phone.unreadCount > 9 ? '9+' : phone.unreadCount }}
      </div>
    </button>

    <div class="h-px w-6 bg-gray-200" />

    <!-- Notes button -->
    <button
      class="flex h-12 w-12 items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors"
      aria-label="Client Notes"
      @click="act(() => emit('toggleNotes'))"
      v-tooltip.left="'Client Notes'"
    >
      <i class="pi pi-pencil text-lg" />
    </button>

    <div class="h-px w-6 bg-gray-200" />

    <!-- Report / Request button. The amber dot quietly signals a captured error
         is available to report; clicking opens the prefilled report dialog. -->
    <button
      class="relative flex h-12 w-12 items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors"
      :aria-label="support.nudge ? 'Something feel off? Report it' : 'Report / Request'"
      @click="act(() => emit('toggleRequest'))"
      v-tooltip.left="support.nudge ? 'Something feel off? Report it' : 'Report / Request'"
    >
      <i class="pi pi-megaphone text-lg" />
      <!-- Pending error-nudge dot -->
      <div
        v-if="support.nudge"
        class="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500"
      />
    </button>
    </div>
    </Transition>
  </div>

  <!-- Report nudge card — sits to the LEFT of the bar, bottom-aligned; slides in
       from the right and retracts to the right (toward the buttons). -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="translate-x-6 opacity-0 scale-95"
    leave-active-class="transition-all duration-300 ease-in"
    leave-to-class="translate-x-6 opacity-0 scale-95"
  >
    <div
      v-if="cardVisible && !barHidden"
      class="fixed bottom-6 right-[80px] z-40 w-52 origin-right overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-2xl"
    >
      <!-- ClaimShield signature seam (same purple→gold as the header line) -->
      <div class="h-[3px] bg-gradient-to-r from-primary-800 via-primary-600 to-accent-500" />
      <div class="px-3 pb-2.5 pt-2.5">
        <div class="flex items-center gap-2">
          <span class="grid h-6 w-6 flex-none place-items-center rounded-md bg-amber-50 text-xs font-bold text-amber-600">?</span>
          <div class="min-w-0 text-[13px] font-semibold leading-snug text-secondary-800">Something feel off?</div>
          <button
            class="-mr-0.5 ml-auto p-0.5 text-secondary-400 transition-colors hover:text-secondary-600"
            @click="dismissCard"
            aria-label="Dismiss"
          >
            <i class="pi pi-times text-[11px]" />
          </button>
        </div>
        <button
          class="mt-2 w-full rounded-md bg-primary-700 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-800"
          @click="emit('toggleRequest')"
        >
          Report it
        </button>
      </div>
    </div>
  </Transition>
</template>
